package org.zest.insan_360_website.data

import com.varabyte.kobweb.browser.storage.StorageKey
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.zest.insan_360_website.data.page_states.UserPreferences
import org.zest.insan_360_website.model.*

val Map<Language, String>.localized: String
    get() = this[UserPreferences.language] ?: this.values.firstOrNull() ?: ""

class UserLanguageKey(name: String) : StorageKey<Language>(name) {
    override fun convertToString(value: Language): String = Json.encodeToString(value)

    override fun convertFromString(value: String): Language? =
        try {
            Json.decodeFromString(value)
        } catch (_: Exception) {
            null
        }
}

val USER_LANGUAGE_KEY = UserLanguageKey("user_language")

@Serializable
enum class Language { EN, BM }

object LanguageSettings {

    fun mapPhrase(en: String, bm: String): Map<Language, String> =
        mapOf(
            Language.EN to en,
            Language.BM to bm
        )

    object General {
        val access_denied_role = mapPhrase("Access Denied for role", "Akses Disekat atas role")
        val networkFailure = mapPhrase(
            "Network error. Please check your connection.",
            "Tidak dapat berhubung. Sila pastikan anda mempunyai hubungan internet"
        )
    }

    object Maintenance {
        val enable_mode = mapPhrase("Enable Maintenance Mode?", "Aktifkan Mod Penyelenggaraan?")
        val disable_mode = mapPhrase("Disable Maintenance Mode?", "Nyahaktifkan Mod Penyelenggaraan?")
        val enable_desc = mapPhrase("All non-super-admin users will see a maintenance page and cannot use the site.", "Semua pengguna bukan super-admin tidak dapat menggunakan tapak web ini.")
        val disable_desc = mapPhrase("The site will be accessible to all users again.", "Tapak web akan boleh diakses oleh semua pengguna semula.")
        val enable = mapPhrase("Enable Maintenance", "Aktifkan Mod Penyelenggaraan")
        val yes_enable = mapPhrase("Yes, Enable Maintenance", "Ya, Aktifkan Mod Penyelenggaraan")
        val disable = mapPhrase("Disable Maintenance", "Nyahaktifkan Mod Penyelenggaraan")
        val yes_disable = mapPhrase("Yes, Disable Maintenance", "Ya, Nyahaktifkan Mod Penyelenggaraan")
        val mode_title = mapPhrase("Maintenance Mode", "Mod Penyelenggaraan")
        val on_status = mapPhrase("ON", "AKTIF")
        val off_status = mapPhrase("OFF", "TIDAK AKTIF")
        val on_desc = mapPhrase(" — Site is in maintenance", " — Laman sedang diselenggara")
        val off_desc = mapPhrase(" — Site is live", " — Laman sedang beroperasi")
        val mode_now_on = mapPhrase("Maintenance mode is now ON", "Mod Penyelenggaraan kini AKTIF")
        val mode_now_off = mapPhrase("Maintenance mode is now OFF", "Mod Penyelenggaraan kini TIDAK AKTIF")
        val error_toggling = mapPhrase("Error toggling maintenance mode: ", "Ralat menukar mod penyelenggaraan: ")
    }

    object AuthenticationPage {
        val mainTitle = mapPhrase("INSAN360 PROFILING SYSTEM", "SISTEM PROFIL INSAN360")
        val mainBullet_1 = mapPhrase("Know Yourself", "Kenali Diri")
        val mainBullet_2 = mapPhrase("Understand Your Team", "Fahami Pasukan")
        val mainBullet_3 = mapPhrase("Improve Performance", "Tingkatkan Prestasi")
        val mainPara = mapPhrase(
            "INSAN360 is an behavioral analysis system based on DISC that helps individuals and " +
                    "organisations understand the communication pattern, motivation, strength and potential" +
                    "for improvement. This system prepares them with practical analysis to assist with building " +
                    "a harmonious working relationships, improve collaboration, and optimize team performance",
            "INSAN360 ialah sistem analisis tingkah laku berasaskan gaya DISC yang membantu" +
                    " individu dan organisasi memahami corak komunikasi, motivasi, kekuatan serta potensi" +
                    " pembangunan diri. Sistem ini menyediakan analisis yang praktikal untuk membina hubungan" +
                    " kerja lebih harmoni, meningkatkan kerjasama dan mengoptimumkan prestasi pasukan."
        )
        val mainList_1 = mapPhrase("DISC Personality Analysis", "Analisis Personaliti DISC")
        val mainListSub_1 = mapPhrase(
            "Gain a deeper understanding of your behavioral style, communication, motivation, and personal tendencies.",
            "Kenali gaya tingkah laku, komunikasi, motivasi dan kecenderungan diri dengan lebih mendalam."
        )
        val mainList_2 = mapPhrase("Professional Report & Action Plan", "Laporan Profesional & Pelan Tindakan")
        val mainListSub_2 = mapPhrase(
            "Receive a comprehensive report alongside clear, practical, and easy-to-implement development strategies.",
            "Dapatkan Laporan komprehensif berserta strategi pembangunan yang jelas, praktikal dan mudah dilaksanakan."
        )
        val mainList_3 = mapPhrase("Individual & Team Transformation", "Transformasi Individu & Pasukan")
        val mainListSub_3 = mapPhrase(
            "Leverage analytical data to improve communication, leadership, team synergy, and organizational productivity.",
            "Gunakan data analisis untuk meningkatkan komunikasi, kepimpinan, keserasian pasukan dan produktiviti organisasi."
        )
        val mainLastSub = mapPhrase(
            "Know Yourself. Transform Your Team. Rise Together.",
            "Kenali Diri Anda. Transformasi Pasukan. Bangkit Bersama"
        )
        val verifiedEmail = mapPhrase(
            "Your email has been successfully verified! You may now log in.",
            "Emel anda telah berjaya disahkan! Anda boleh log masuk sekarang."
        )
        val loginRequired = mapPhrase("Please log in to access the site.", "Sila log masuk untuk mengakses laman ini.")
        val welcome = mapPhrase("Welcome to Insan360", "Selamat datang ke Insan360")
        val beginJourney = mapPhrase("Begin your journey here at Insan360!", "Mulakan Perjalanan Anda di Insan360!")
        val email = mapPhrase("Email Address", "Alamat Emel")
        val fullName = mapPhrase("Full Name", "Nama Penuh")
        val ic = mapPhrase("Identification Card Number", "Nombor Kad Pengenalan")
        val addressLine = mapPhrase("Address Line", "Alamat Baris")
        val city = mapPhrase("City", "Bandar")
        val state = mapPhrase("State", "Negeri")
        val postalCode = mapPhrase("Postal Code", "Poskod")
        val country = mapPhrase("Country", "Negara")
        val phone = mapPhrase("Phone Number", "Nombor Telefon")
        val work = mapPhrase("Work Type", "Jenis Pekerjaan")
        val position = mapPhrase("Position", "Jawatan")
        val password = mapPhrase("Password", "Kata Laluan")
        val proceed = mapPhrase("Proceed", "Seterusnya")
        val back = mapPhrase("Back", "Kembali")
        val individual = mapPhrase("Individual", "Persendirian")
        val organization = mapPhrase("Organization", "Organisasi")
        val confirmPassword = mapPhrase("Confirm Password", "Sahkan Kata Laluan")
        val passwordConfirmation = mapPhrase("Please confirm your password.", "Sila sahkan kata laluan anda.")
        val passwordsDoNotMatch = mapPhrase("Passwords do not match.", "Kata laluan tidak sepadan.")
        val enterPasswordAndConfirmation = mapPhrase("Please enter password and confirmation.", "Sila masukkan kata laluan dan pengesahan.")
        val signup = mapPhrase("Sign Up", "Daftar")
        val login = mapPhrase("Login", "Log Masuk")
        val noAccount = mapPhrase("Don't have an account? Register here.", "Tiada akaun? Tekan link untuk daftar.")
        val haveAccount =
            mapPhrase("Already have an account? Log in here.", "Sudah mempunyai akaun? Tekan link untuk log masuk.")
        val enterEmailPassword = mapPhrase("Please enter email and password.", "Sila masukkan emel dan kata laluan.")
        val enterValidEmail = mapPhrase("Please enter a valid email", "Sila masukkan emel yang sah")
        val fillAllRequiredFields = mapPhrase("Please fill in all required fields.", "Sila isi semua ruangan yang wajib.")
        val forgotPassword = mapPhrase("Forgot Password?", "Lupa Kata Laluan?")
        val resetPassword = mapPhrase("Reset Password", "Tetapkan Semula Kata Laluan")
        val sendResetLink = mapPhrase("Send Reset Link", "Hantar Pautan")
        val resetEmailSent = mapPhrase("Reset link sent.", "Pautan tetapan semula telah dihantar.")
        val backToLogin = mapPhrase("Back to Login", "Kembali ke Log Masuk")
        val invalidLink = mapPhrase("Invalid or missing link.", "Pautan tidak sah atau tiada.")
        val invalidToken = mapPhrase("Invalid token.", "Token tidak sah.")
        val passwordLengthError = mapPhrase("Password must be at least 8 characters long.", "Kata laluan mestilah sekurang-kurangnya 8 aksara.")
        val passwordComplexError = mapPhrase("Password must be at least 8 characters long and contain at least one uppercase letter and one number.", "Kata laluan mestilah sekurang-kurangnya 8 aksara dan mengandungi sekurang-kurangnya satu huruf besar dan satu nombor.")
        val newPassword = mapPhrase("New Password", "Kata Laluan Baru")
        val currentPassword = mapPhrase("Current Password", "Kata Laluan Semasa")
        val changePassword = mapPhrase("Change Password", "Tukar Kata Laluan")
        val updatePassword = mapPhrase("Update Password", "Kemaskini Kata Laluan")
        val fillOrgAndPosition = mapPhrase("Please fill in Organization and Position.", "Sila isi Organisasi dan Jawatan.")
        val nameFormatError = mapPhrase("Name must contain only letters, hyphens, apostrophes and minimum 3 characters.", "Nama mestilah mengandungi huruf, tanda sempang, apostrof sahaja dan sekurang-kurangnya 3 aksara.")
        val emailFormatError = mapPhrase("Please enter a valid email address.", "Sila masukkan alamat emel yang sah.")
        val nricFormatError = mapPhrase("National ID must be exactly 12 digits (no dashes).", "ID Kebangsaan mestilah tepat 12 digit (tanpa sempang).")
        val phoneFormatError = mapPhrase("Please enter a valid phone number (10-15 digits).", "Sila masukkan nombor telefon yang sah (10-15 digit).")
        val postalCodeError = mapPhrase("Please enter a valid postal code.", "Sila masukkan poskod yang sah.")
        val orgNameError = mapPhrase("Organization name must contain only valid characters.", "Nama organisasi mestilah mengandungi aksara yang sah sahaja.")
        val positionError = mapPhrase("Position must contain only valid characters.", "Jawatan mestilah mengandungi aksara yang sah sahaja.")
    }

    object AssessmentPage {
        val starting = mapPhrase("Starting Assessment", "Memulakan Ujian")
        val has_started = mapPhrase("Assessment has been started", "Ujian telah dimulakan")
        val continuing = mapPhrase("Continuing Test", "Menyambung Ujian")

        val question = mapPhrase("Question", "Soalan")
        val next = mapPhrase("Next", "Seterusnya")
        val previous = mapPhrase("Previous", "Sebelum")
        val finish = mapPhrase("Finish", "Tamat")
        val more = mapPhrase("More", "Lebih")
        val less = mapPhrase("Less", "Kurang")
        val questions_index = mapPhrase("Question Index", "Indeks Soalan")

        object SessionExpiredModal {
            val timer_run_out = mapPhrase("It seems like the timer has run out.", "Nampaknya masa anda telah tamat.")
            val continue_extend_or_exit = mapPhrase(
                "To continue, you can extend your timer by clicking extend or you can exit the assessment",
                "Untuk meneruskan ujian, anda boleh menambah masa dengan butang tambah masa atau anda boleh keluar daripada sesi ujian"
            )
            val exit_warning = mapPhrase(
                "(if you exit before finishing the test it will consume your credit and you will need to get a new credit to start another assessment)",
                "(jika anda keluar sebelum melengkapkan ujian, kredit anda akan ditolak dan anda perlu mendapatkan kredit baru untuk memulakan ujian lain)"
            )
            val exiting = mapPhrase("Exiting...", "Keluar...")
            val exit_session = mapPhrase("Exit the session", "Keluar sesi")
            val extending = mapPhrase("Extending...", "Menambah...")
            val extend = mapPhrase("Extend", "Tambah Masa")
            val timer_completely_run_out = mapPhrase("Your timer has completely run out!", "Masa anda telah tamat!")
            val unable_to_finish = mapPhrase(
                "You are unable to finish the assessment in the allocated time",
                "Anda tidak dapat melengkapkan ujian dalam masa yang ditetapkan"
            )
            val enquiries_contact_us =
                mapPhrase("For any enquiries, please do contact us.", "Untuk sebarang pertanyaan, sila hubungi kami.")
        }
    }

    object UserDashboardPage {
        // Page Layout
        val home = mapPhrase("Home", "Laman Utama")
        val report = mapPhrase("Assessment Report", "Laporan Ujian")
        val settings = mapPhrase("Settings", "Tetapan")
        val support = mapPhrase("Customer Support", "Khidmat Pelanggan")
        val credits = mapPhrase("Credit Handling", "Pengendalian Kredit")
        val students = mapPhrase("Members Handling", "Pengendalian Ahli")
        val admins = mapPhrase("Admin Handling", "Pengendalian Admin")
        val studentAi = mapPhrase("Life Coach", "Jurulatih Kehidupan")
        val adminAi = mapPhrase("Coach Assistant", "Pembantu Jurulatih")

        // Main Page
        val loading = mapPhrase("Loading...", "Mengemaskini...")
        val personality_test = mapPhrase("Personality Test", "Ujian Personaliti")
        val not_enough_credit = mapPhrase("Not Enough Credit", "Kredit Tidak Mencukupi")
        val begin_test = mapPhrase("Begin Test", "Mula Ujian")
        val continue_test = mapPhrase("Continue", "Sambung")
        val need_active_credit =
            mapPhrase("Need an active credit to start the assessment", "Kredit aktif diperlukan untuk mula ujian")
        val current_credit_status = mapPhrase("Current Credit Status", "Status Kredit Terkini")
        val buy_credit = mapPhrase("Buy Credit", "Beli Kredit")
        val credit_state = mapOf(
            CreditStatus.ACTIVE to mapPhrase("Active", "Aktif"),
            CreditStatus.IN_PROGRESS to mapPhrase("In Progress", "Dalam Proses"),
            CreditStatus.USED to mapPhrase("Used", "Telah Digunakan"),
            CreditStatus.EXPIRED to mapPhrase("Expired", "Tamat tempoh"),
            null to mapPhrase("Inactive", "Tidak Aktif")
        )
        val credit_purchase_record = mapPhrase(
            "Credit Purchase Record",
            "Rekod Pembelian Kredit"
        )
        val only_one_credit = mapPhrase(
            "User can only have one active credit at a time",
            "Pengguna hanya dibenarkan memiliki satu kredit aktif pada satu masa"
        )
        val please_finish_test = mapPhrase(
            "Please submit unfinished test before purchasing new credit",
            "Sila selesaikan ujian sebelum membeli kredit baru"
        )
        val user_with_org_not_allowed = mapPhrase(
            "User under organization are not allowed to purchase credit",
            "Pengguna dengan tanggungan organisasi tidak dibernakan membeli kredit"
        )
        val no_credit = mapPhrase("No Credit", "Tiada Kredit")
        val id_credit = mapPhrase("Credit ID", "ID Kredit")
        val id_transaction = mapPhrase("Transaction ID", "ID Transaksi")
        val id_toyyib = mapPhrase("Toyyibpay ID", "ID Toyyibpay")
        val time_transaction = mapPhrase("Time of transaction", "Waktu Transaksi")
        val not_used = mapPhrase("Not used", "Belum Digunakan")
        val time_used = mapPhrase("Time of use", "Waktu Digunakan")
        val creditHistoryState = mapOf(
            "PAID" to mapPhrase("PAID", "DIBAYAR"),
            "ACTIVE" to mapPhrase("ACTIVE", "AKTIF"),
            "PENDING" to mapPhrase("PENDING", "TIDAK AKTIF"),
            "IN_PROGRESS" to mapPhrase("IN PROGRESS", "SEDANG DIGUNAKAN"),
            "FAILED" to mapPhrase("FAILED", "GAGAL"),
            "EXPIRED" to mapPhrase("EXPIRED", "TAMAT TEMPOH"),
            "USED" to mapPhrase("USED", "TELAH DIGUNAKAN"),
            "ERROR" to mapPhrase("ERROR", "ERROR")
        )
        val id_copied = mapPhrase("ID Copied", "ID Disalin")
        val email_copied = mapPhrase("Email Copied", "E-mel Disalin")
        val org_member = mapPhrase("Organization Member", "Ahli Organisasi Anda")
        val test_taken = mapPhrase("Test Taken", "Ujian Yang Telah Diambil")
        val test_need_verify = mapPhrase("Test Verification Needed", "Ujian Yang Perlu Disahkan")
        val org_credit_info = mapPhrase("Organization Credit Info", "Maklumat Kredit Organisasi")
        val org_credit = mapPhrase("Organization Credit", "Kredit Organisasi")
        val active = mapPhrase("Active", "Aktif")
        val used = mapPhrase("Used", "Telah Digunakan")
        val expired = mapPhrase("Expired", "Telah Tamat Tempoh")
        val unassigned = mapPhrase("Unassigned", "Belum Diagih")
        val total_user = mapPhrase("Total Client", "Jumlah Pengguna")
        val admin_need_verify = mapPhrase("Admin Verification Needed", "Pengesahan Admin Diperlukan")
        val total_admin_org = mapPhrase("Total Organization & Admin", "Jumlah Organisasi & Admin")
        val system_credit_info = mapPhrase("System Credit Info", "Maklumat Kredit Sistem")
        val system_credit = mapPhrase("System Total Count", "Jumlah Dalam Sistem")
        val give_credit_to_user = mapPhrase("Grant Credit To User", "Beri Kredit Kepada Pengguna")
        val give_credit = mapPhrase("Grant Credit", "Beri Kredit")
        val please_enter_user_email = mapPhrase("Please enter user's email", "Sila masukkan email pengguna")
        val credit_more_than_0 = mapPhrase("Credit has to be more than 0", "Kredit perlu lebih dari 0")
        val valid_email = mapPhrase("Please enter a valid email", "Pastikan emel yang sah")

        object GiveCreditModal {
            val title = mapPhrase(
                "Granting Assessment Credit",
                "Pemberian Kredit Ujian"
            )
            val text = mapPhrase(
                "Please enter the email of admin or client that will be granted the credit. Client can only be granted a single credit",
                "Sila masukkan alamat emel pengguna atau admin yang akan diberi kredit. Pengguna biasa hanya boleh diberikan satu kredit sahaja"
            )
            val grant = mapPhrase("Grant", "Beri")
            val credit = mapPhrase("Credit", "Kredit")
            val generating_credit = mapPhrase("Generating Credit...", "Memberi Kredit...")
        }

        object PaymentModal {
            val title = mapPhrase("Purchase Assessment Credit", "Beli Kredit Ujian")
            val text = mapPhrase(
                "Secure checkout via FPX Online Banking. You will be redirected to your banking provider to authorize the transaction.",
                "Pembayaran melalui FPX. Anda akan dihantar ke laman bank anda untuk meluluskan transaksi."
            )
            val connecting = mapPhrase(
                "Connecting to Bank Gateway...",
                "Menghubung ke Gerbang Bank..."
            )
            val pay_with = mapPhrase(
                "Pay with FPX (RM 20)",
                "Bayar dengan FPX (RM 20)"
            )
            val already_under_org = mapPhrase(
                "User is already under an organization",
                "Pengguna telah berdaftar dibawah organisasi"
            )
            val super_not_required = mapPhrase(
                "Super Admin are not required to purchase credit",
                "Super Admin tidak perlu membeli kredit"
            )
            val email_not_found = mapPhrase(
                "User email not found. Cannot proceed to checkout.",
                "Emel Pengguna tidak dijumpai. Tidak boleh meneruskan pembelian"
            )
            val token_not_found = mapPhrase(
                "User token not found. Cannot proceed to checkout.",
                "Token Pengguna tidak dijumpai. Tidak boleh meneruskan pembelian"
            )
            val initiation_failed = mapPhrase(
                "Failed to initialize gateway session.",
                "Gagal memulakan sesi pembayaran"
            )
        }

        object VideoModal {
            val title = mapPhrase(
                "Please watch the briefing before proceeding",
                "Sila lihat video ini sebelum mula"
            )
            val finish_video = mapPhrase(
                "Finish the video to proceed",
                "Tamatkan video untuk mula"
            )
            val start_test = mapPhrase(
                "Start Assessment",
                "Mulakan Ujian"
            )
            val watch_later = mapPhrase(
                "Watch Later",
                "Tonton Kemudian"
            )
            val video_counter = mapPhrase(
                "Video {1} of {2}",
                "Video {1} daripada {2}"
            )
            val unsupported_video = mapPhrase(
                "Your browser does not support the video tag.",
                "Pelayar anda tidak menyokong tag video ini."
            )
        }

        //Credit Handling Page
        val organization_credit_calculation = mapPhrase("Organization Credit Calculation", "Perkiraan Kredit Organisasi")
        val total = mapPhrase("Total", "Jumlah")
        val assigned = mapPhrase("Assigned", "Sudah Diagih")
        val credit = mapPhrase("Credit", "Kredit")
        val from = mapPhrase("From", "Dari")
        val to = mapPhrase("To", "Hingga")
        val paymentStatus = mapOf(
            PaymentStatus.PAID to mapPhrase("PAID", "DIBAYAR"),
            PaymentStatus.PENDING to mapPhrase("PENDING", "DALAM PROSES"),
            PaymentStatus.FAILED to mapPhrase("FAILED", "GAGAL")
        )
        val system_credit_purchase_record = mapPhrase("System Credit Purchase Record", "Rekod Pembelian Kredit Sistem")
        val no_affiliation = mapPhrase("No Affiliation", "Persendirian")

        object BulkPurchaseModal {
            val title = mapPhrase("Purchase Assessment Credit", "Beli Kredit Ujian")
            val sub = mapPhrase(
                "Secure checkout via FPX Online Banking. You will be redirected to your banking provider to authorize the transaction. (limit is 100 credit per transaction)",
                "Pembayaran melalui FPX. Anda akan dihantar ke laman bank anda untuk meluluskan transaksi. (had adalah 100 kredit setiap transaksi)"
            )
            val purchase = mapPhrase("Purchase", "Pembelian")
            val credit = mapPhrase("Credit(s)", "Kredit")
            val pay_with_fpx = mapPhrase("Pay with FPX", "Bayar dengan FPX")
        }
        val testSubmissionFilterOptions = mapOf(
            "ALL" to mapPhrase("All", "Semua"),
            "PENDING" to mapPhrase("Pending", "Menunggu Pengesahan"),
            "VERIFIED" to mapPhrase("Verified", "Telah Disahkan"),
            "REJECTED" to mapPhrase("Rejected", "Ditolak")
        )
        val testSubmissionState = mapOf(
            TestSubmissionStatus.PENDING to mapPhrase("PENDING", "MENUNGGU PENGESAHAN"),
            TestSubmissionStatus.VERIFIED to mapPhrase("VERIFIED", "TELAH DISAHKAN"),
            TestSubmissionStatus.REJECTED to mapPhrase("REJECTED", "DITOLAK")
        )
        val no_submissions = mapPhrase("No submissions found", "Tiada ujian dijumpai")
        val name = mapPhrase("Name", "Nama")
        val email = mapPhrase("Email", "Emel")
        val phone_number = mapPhrase("Phone No", "No Telefon")
        val search = mapPhrase("Search...", "Cari...")
        val latest = mapPhrase("Latest", "Terkini")
        val history = mapPhrase("Past", "Lepas")
        val organization_name = mapPhrase("Organization", "Organisasi")
        val date_answered = mapPhrase("Date Answered", "Tarikh Dijawab")
        val download_report = mapPhrase("Download Report", "Muat Turun Laporan")
        val request_report = mapPhrase("Request Report", "Mohon Laporan")
        val request_pending = mapPhrase("Request Pending", "Permohonan Sedang Diproses")
        val test_id = mapPhrase("Test ID", "ID Ujian")
        val credit_id = mapPhrase("Credit ID", "ID Kredit")
        val answered_under = mapPhrase("Answered under", "Dijawab dibawah")
        val close = mapPhrase("Close", "Tutup")
        val submission_date = mapPhrase("Submission Date", "Tarikh Dihantar")

        object ReportDetail {
            val select_template = mapPhrase("Select a template", "Pilih templat")
            val view_only = mapPhrase("View Only", "Paparan Sahaja")
            val verify = mapPhrase("Verify", "Sahkan")
            val reject = mapPhrase("Reject", "Tolak")
            val generate_report = mapPhrase("Generate Report", "Jana Laporan")
            val report_expires_on = mapPhrase("Expires on: ", "Tamat pada: ")
            val user_requested = mapPhrase("User requested regeneration", "Pengguna memohon jana semula")
            val identification_number = mapPhrase("Identification Number", "Nombor Kad Pengenalan")
            val phone_number = mapPhrase("Phone Number", "Nombor Telefon")
            val date = mapPhrase("Date", "Tarikh")
        }

        // Student Handling Page
        val student_and_invite_list = mapPhrase("Member & Invitation List", "Senarai Ahli & Jemputan")
        val no_members_or_invites =
            mapPhrase("No members or invites to organization", "Tiada ahli atau jemputan dalam organisasi")
        val cancel = mapPhrase("Cancel", "Batal")
        val send = mapPhrase("Send", "Hantar")
        val confirm = mapPhrase("Confirm", "Sahkan")
        val invite = mapPhrase("Invite", "Jemput")
        val member_email_placeholder = mapPhrase("Member's email...", "Emel ahli...")
        val enter_member_email = mapPhrase("Please enter member's email", "Sila masukkan emel ahli")
        val ensure_valid_email = mapPhrase("Please ensure valid email", "Pastikan emel yang sah")
        val cannot_invite_self = mapPhrase("You cannot invite yourself", "Anda tidak boleh menjemput diri sendiri")
        val studentStatus = mapOf(
            StudentStatus.VERIFIED to mapPhrase("VERIFIED", "TELAH DISAHKAN"),
            StudentStatus.ASSIGNED to mapPhrase("ACTIVE", "AKTIF"),
            StudentStatus.IN_PROGRESS to mapPhrase("IN PROGRESS", "SEDANG MENJAWAB"),
            StudentStatus.PENDING to mapPhrase("PENDING", "MENUNGGU PENGESAHAN"),
            StudentStatus.REJECTED to mapPhrase("REJECTED", "DITOLAK"),
            StudentStatus.UNASSIGNED to mapPhrase("UNASSIGNED", "BELUM DIAGIH")
        )
        val inviteStatus = mapOf(
            InviteStatus.ACCEPTED to mapPhrase("ACCEPTED", "TELAH DISAHKAN"),
            InviteStatus.INVITED to mapPhrase("INVITED", "DIHANTAR"),
            InviteStatus.EXPIRED to mapPhrase("EXPIRED", "TAMAT TEMPOH")
        )
        val assign_credit = mapPhrase("Assign Credit", "Agihkan Kredit")

        // Admin Handling Page
        val admin_verification = mapPhrase("Admin Verification", "Pengesahan Admin")
        val admin_list = mapPhrase("Admin List", "Senarai Admin")
        val organization = mapPhrase("Organization", "Organisasi")
        val no_admin_verification_needed = mapPhrase("No admin verification needed", "Tiada admin yang perlu disahkan")
        val no_verified_admin_found = mapPhrase("No verified admin found", "Tiada admin sah dijumpai")
        val no_results_for = mapPhrase("No results for", "Tiada carian")

        object AdminInfoModal {
            val verification_state = mapOf(
                true to mapPhrase("VERIFIED", "TELAH DISAHKAN"),
                false to mapPhrase("NEED VERIFICATION", "BELUM DISAHKAN")
            )
            val personal_details = mapPhrase("Personal Details", "Maklumat Peribadi")
            val organization_details = mapPhrase("Organization Details", "Maklumat Organisasi")
            val verify_admin = mapPhrase("Verify Admin", "Sahkan Admin")
            val verifying = mapPhrase("Verifying...", "Mengesahkan...")
        }

        // Settings Page
        val language_preferences = mapPhrase("Language Preferences", "Pilih Terjemahan")
        val language_preferences_detail = mapPhrase(
            "Choose your preferred language for the interface",
            "Pilih terjemahan untuk laman ini"
        )
        val account_security = mapPhrase("Account Security", "Sekuriti Akaun")
        val account_security_detail = mapPhrase(
            "Sign out of your current session on this device",
            "Log keluar dari sesi anda untuk peranti ini"
        )
        val logout = mapPhrase("Logout", "Log Keluar")

        object LogoutModal {
            val title = mapPhrase("Are you sure you want to logout?", "Anda pasti untuk log keluar?")
            val cancel = mapPhrase("Cancel", "Batal")
            val yes = mapPhrase("Yes, Logout", "Ya, Log Keluar")
        }
    }

    object ResultPage {
        val back_home = mapPhrase("Back Home", "Laman Utama")
        val video_modal_title = mapPhrase(
            "Please watch the video while your results are processed",
            "Sila lihat video ini sementara keputusan anda diproses"
        )
        val video_modal_finish = mapPhrase(
            "Finish video",
            "Tamatkan video"
        )
        val video_modal_view_results = mapPhrase(
            "View Results",
            "Lihat Keputusan"
        )
        val graphLabel = mapOf(
            GraphType.More to mapPhrase("PUBLIC SELF", "PENGUBAHSUAIAN"),
            GraphType.Less to mapPhrase("PRIVATE SELF", "PENSTABILAN"),
            GraphType.Difference to mapPhrase("PERCEIVED SELF", "SEMULAJADI")
        )
        val submit_failed_toast = mapPhrase(
            "Failed to submit test results. Please check your connection and try again.",
            "Gagal menghantar keputusan ujian. Sila periksa sambungan internet anda dan cuba lagi."
        )
        val thank_you = mapPhrase(
            "Thank you for submitting your answer. It will be validated later by your coach.",
            "Terima kasih kerana melengkapkan ujian anda. Ia akan disahkan oleh jurulatih anda."
        )
        val error_submitting = mapPhrase(
            "We encountered an error submitting your results.",
            "Masalah semasa menghantar keputusan anda."
        )
        val retry_submission = mapPhrase(
            "Retry Submission",
            "Cuba Semula"
        )
        val please_wait = mapPhrase(
            "Please wait until your answer has been submitted. Please do not close or reload the page.",
            "Sila tunggu sehingga jawapan anda selesai dihantar. Tolong jangan tutup atau muat semula halaman ini."
        )
        val error_unfulfilled = mapPhrase(
            "Error, Unfulfilled Assessment",
            "Amaran, Ujian Tidak Lengkap"
        )
        val go_back = mapPhrase(
            "Go Back",
            "Kembali"
        )
        val submitting = mapPhrase(
            "Submitting your test results...",
            "Sedang menghantar keputusan ujian anda..."
        )
        val retrying = mapPhrase(
            "Retrying submission...",
            "Mencuba semula..."
        )
    }

    object ResponseError {
        val session_expired =
            mapPhrase("Session expired, please log in again.", "Sesi sudah berakhir, sila log masuk semula.")

        object MainDash {
            val grant_credit = mapPhrase(
                "Error granting credit",
                "Gagal memberi kredit"
            )
            val start_test = mapPhrase(
                "Error when starting assessment",
                "Gagal memulakan ujian"
            )
        }
    }

    object ApiErrors {
        private val translations = mapOf(
            ApiErrorCode.INVALID_CREDENTIALS to mapPhrase(
                "Invalid Email or Password.",
                "Emel atau kata laluan tidak sah."
            ),
            ApiErrorCode.EMAIL_NOT_VERIFIED to mapPhrase(
                "Please verify your email before logging in.",
                "Sila sahkan emel anda sebelum log masuk."
            ),
            ApiErrorCode.MUST_REGISTER_AS_STUDENT to mapPhrase(
                "You must register as a student under your organization.",
                "Anda mesti mendaftar sebagai pelajar di bawah organisasi anda."
            ),
            ApiErrorCode.TOKEN_VERIFICATION_FAILED to mapPhrase(
                "Your session has expired. Please log in again.",
                "Sesi anda telah tamat tempoh. Sila log masuk semula."
            ),
            ApiErrorCode.UNAUTHORIZED_ROLE_ACCESS to mapPhrase(
                "You do not have permission to access this resource.",
                "Anda tidak mempunyai kebenaran untuk mengakses sumber ini."
            ),
            ApiErrorCode.USER_NOT_FOUND to mapPhrase("User not found.", "Pengguna tidak dijumpai."),
            ApiErrorCode.EMAIL_ALREADY_EXISTS to mapPhrase(
                "This email is already registered.",
                "Emel ini telah pun didaftarkan."
            ),
            ApiErrorCode.ORG_NAME_REQUIRED to mapPhrase(
                "Organization name is required.",
                "Nama organisasi diperlukan."
            ),
            ApiErrorCode.INVALID_VERIFICATION_LINK to mapPhrase(
                "Invalid or expired verification link.",
                "Pautan pengesahan tidak sah atau telah luput."
            ),
            ApiErrorCode.MISSING_REQUEST_BODY to mapPhrase(
                "Incomplete data submitted. Please try again.",
                "Data yang dihantar tidak lengkap. Sila cuba lagi."
            ),
            ApiErrorCode.INVALID_RESET_TOKEN to mapPhrase(
                "Invalid or malformed reset token.",
                "Pautan tetapan semula tidak sah."
            ),
            ApiErrorCode.RESET_TOKEN_EXPIRED to mapPhrase(
                "This reset link has expired. Please request a new one.",
                "Pautan tetapan semula ini telah luput. Sila mohon yang baru."
            ),
            ApiErrorCode.INTERNAL_SERVER_ERROR to mapPhrase(
                "Something went wrong on our end. Please try again later.",
                "Terdapat ralat pada sistem kami. Sila cuba sebentar lagi."
            ),
            ApiErrorCode.ACTION_FAILED to mapPhrase(
                "The requested action failed to complete.",
                "Tindakan yang diminta gagal diselesaikan."
            ),

            ApiErrorCode.ORGANIZATION_NOT_FOUND to mapPhrase("Organization not found.", "Organisasi tidak dijumpai."),
            ApiErrorCode.DATA_INTEGRITY_MISMATCH to mapPhrase(
                "Data integrity mismatch.",
                "Ketidaksamaan integriti data."
            ),
            ApiErrorCode.INVALID_CREDIT_COUNT to mapPhrase("Invalid credit count.", "Bilangan kredit tidak sah."),
            ApiErrorCode.SUPER_ADMIN_NOT_FOUND to mapPhrase("Super admin not found.", "Super admin tidak dijumpai."),
            ApiErrorCode.SUPER_ADMIN_CREDIT_RESTRICTION to mapPhrase(
                "Super admins cannot be assigned credits.",
                "Kredit tidak boleh diagihkan kepada Super admin."
            ),
            ApiErrorCode.STUDENT_CREDIT_LIMIT_REACHED to mapPhrase(
                "Student credit limit reached.",
                "Had kredit pelajar telah dicapai."
            ),
            ApiErrorCode.UNFULFILLED_CREDIT_EXISTS to mapPhrase(
                "Unfulfilled credit already exists.",
                "Kredit tidak selesai masih ada."
            ),
            ApiErrorCode.UNAUTHORIZED_CREDIT_ACCESS to mapPhrase(
                "Unauthorized credit access.",
                "Akses kredit tidak sah."
            ),
            ApiErrorCode.INVALID_STATUS_TRANSITION to mapPhrase(
                "Invalid credit status transition.",
                "Pertukaran status kredit tidak sah."
            ),
            ApiErrorCode.NO_ELIGIBLE_CREDIT_FOUND to mapPhrase("No eligible credit found.", "Tiada kredit Sah."),
            ApiErrorCode.MUST_RESOLVE_PENDING_TEST to mapPhrase(
                "Please resolve pending test first.",
                "Sila selesaikan ujian yang belum selesai dahulu."
            ),
            ApiErrorCode.CREDIT_STATUS_CONCURRENT_MODIFICATION to mapPhrase(
                "Credit status modified concurrently.",
                "Status kredit telah serentak."
            ),
            ApiErrorCode.COOLDOWN_PERIOD_ACTIVE to mapPhrase(
                "You cannot purchase a credit yet. You must wait 6 months after your last test.",
                "Anda tidak boleh membeli kredit lagi. Sila tunggu 6 bulan selepas ujian terakhir anda."
            ),
            ApiErrorCode.ADMIN_COOLDOWN_ACTIVE to mapPhrase(
                "Cannot grant credit. This member must wait 6 months after their last test before taking another.",
                "Tidak boleh memberi kredit. Ahli ini mesti menunggu 6 bulan selepas ujian terakhir mereka."
            ),
            ApiErrorCode.MAINTENANCE_MODE to mapPhrase(
                "System is currently under maintenance. Please try again later.",
                "Sistem sedang diselenggara. Sila cuba sebentar lagi."
            ),
            ApiErrorCode.RATE_LIMIT_EXCEEDED to mapPhrase(
                "You are performing this action too quickly. Please wait a moment and try again.",
                "Anda melakukan tindakan ini terlalu pantas. Sila tunggu sebentar dan cuba lagi."
            ),
            ApiErrorCode.ACCOUNT_LOCKED to mapPhrase(
                "Account temporarily locked due to too many failed login attempts. Please try again in 15 minutes.",
                "Akaun dikunci sementara waktu akibat terlalu banyak percubaan log masuk yang gagal. Sila cuba lagi dalam 15 minit."
            )
        )

        fun getMessage(errorCode: ApiErrorCode?, language: Language): String {
            if (errorCode == null) return mapPhrase(
                "An unexpected error occurred.",
                "Ralat yang tidak dijangka telah berlaku."
            )[language]!!

            return translations[errorCode]?.get(language)
                ?: mapPhrase("An unexpected error occurred.", "Ralat yang tidak dijangka telah berlaku.")[language]!!
        }
    }

    object ApiSuccess {
        private val translations = mapOf(
            ApiSuccessCode.ACCOUNT_CREATED to mapPhrase("Account Created Successfully", "Akaun Berjaya Dicipta"),
            ApiSuccessCode.LOGIN_SUCCESSFUL to mapPhrase("Login successful", "Log masuk berjaya"),
            ApiSuccessCode.LOGOUT_SUCCESSFUL to mapPhrase("Logged out successfully", "Log keluar berjaya"),
            ApiSuccessCode.PASSWORD_RESET_EMAIL_SENT to mapPhrase("Reset link has been sent.", "Pautan tetapan semula telah dihantar."),
            ApiSuccessCode.PASSWORD_RESET_SUCCESSFUL to mapPhrase("Password successfully updated. Please log in again.", "Kata laluan berjaya dikemaskini. Sila log masuk semula."),
            ApiSuccessCode.GENERAL_SUCCESS to mapPhrase(
                "Action completed successfully",
                "Tindakan berjaya diselesaikan"
            ),

            ApiSuccessCode.STATS_LOADED_SUCCESSFULLY to mapPhrase(
                "Dashboard stats loaded successfully.",
                "Statistik berjaya diproses"
            ),
            ApiSuccessCode.CREDIT_GRANTED_SUCCESSFULLY to mapPhrase(
                "Credit granted successfully.",
                "Kredit berjaya diagih"
            ),
            ApiSuccessCode.CREDIT_HISTORY_FETCHED_SUCCESSFULLY to mapPhrase(
                "Credit history fetched successfully.",
                "Rekod kredit berjaya diproses"
            ),
            ApiSuccessCode.CREDIT_STATUS_UPDATED_SUCCESSFULLY to mapPhrase(
                "Credit status updated successfully.",
                "Status kredit dikemaskini"
            ),
            ApiSuccessCode.MAINTENANCE_STATUS_FETCHED to mapPhrase(
                "Maintenance status fetched successfully.",
                "Status penyelenggaraan berjaya didapatkan."
            ),
            ApiSuccessCode.MAINTENANCE_STATUS_UPDATED to mapPhrase(
                "Maintenance status updated successfully.",
                "Status penyelenggaraan berjaya dikemaskini."
            )
        )

        fun getMessage(successCode: ApiSuccessCode?, language: Language): String {
            if (successCode == null) return mapPhrase("Success", "Berjaya")[language]!!
            return translations[successCode]?.get(language) ?: mapPhrase("Success", "Berjaya")[language]!!
        }
    }
}